import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BRAND } from '@saesak/shared';
import { SiteFooter } from './SiteFooter';

/**
 * 서비스 이용약관 — 공개 페이지 (전자상거래법·PG 심사 요건).
 * 환불 규정(제8조)은 #refund 앵커로 직접 연결된다.
 */

const EFFECTIVE_DATE = '2026년 8월 2일';

function Article({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mt-7 scroll-mt-6">
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="mt-2 flex flex-col gap-2 text-base leading-relaxed text-ink">{children}</div>
    </section>
  );
}

export function TermsPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView();
    }
  }, [location.hash]);

  return (
    <main className="mx-auto w-full max-w-screen-sm px-6 py-10">
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <img src="/brand-icon.svg" alt="" width={56} height={56} />
        <h1 className="text-2xl font-bold">서비스 이용약관</h1>
        <p className="text-sm text-ink-sub">
          {BRAND.name} · 시행일 {EFFECTIVE_DATE}
        </p>
      </div>

      <Article title="제1조 (목적)">
        <p>
          이 약관은 새싹돋보기(이하 "서비스") 운영자(이하 "회사")가 제공하는 보육기관–가정
          소통·기록 서비스의 이용 조건과 절차, 회사와 이용자의 권리·의무를 정함을 목적으로
          합니다.
        </p>
      </Article>

      <Article title="제2조 (정의)">
        <p>
          ① "기관"은 서비스 이용 계약을 체결한 어린이집·유치원 등 보육기관을 말합니다.
          ② "이용자"는 기관의 구성원(원장·교직원)과 기관에 연결된 보호자를 말합니다.
          ③ "구독"은 기관 단위로 체결되는 유료 이용 계약을 말합니다.
        </p>
      </Article>

      <Article title="제3조 (약관의 효력과 변경)">
        <p>
          ① 이 약관은 서비스 화면에 게시함으로써 효력이 생깁니다. ② 회사는 관계 법령을
          위반하지 않는 범위에서 약관을 변경할 수 있으며, 변경 시 적용일 7일 전(이용자에게
          불리한 변경은 30일 전)부터 공지사항으로 알립니다.
        </p>
      </Article>

      <Article title="제4조 (서비스의 내용)">
        <p>
          회사는 알림장·앨범·출석·발달기록·생활기록부·공지·쪽지·등하원 버스 등 보육기관과
          가정의 소통·기록 기능을 웹과 모바일 앱으로 제공합니다. 구체적 기능은 서비스 화면과
          요금 안내에 표시합니다.
        </p>
      </Article>

      <Article title="제5조 (이용 계약의 성립)">
        <p>
          ① 기관 구독 계약은 기관의 신청과 회사의 승인으로 성립하며, 승인 시 무료 체험
          기간이 제공될 수 있습니다. ② 개인 이용자는 기관이 발급한 초대코드로 가입하며,
          가입 시 이 약관과 개인정보 처리방침에 동의한 것으로 봅니다. ③ 만 14세 미만
          아동은 직접 가입할 수 없으며, 아동에 관한 기록은 보호자 동의 아래 기관이
          작성·관리합니다.
        </p>
      </Article>

      <Article title="제6조 (이용료와 결제)">
        <p>
          ① 서비스 이용료는 기관 단위 연간 구독제이며, 금액과 산정 기준(기본료·원아 수
          구간)은 요금 안내 페이지에 표시합니다. ② 결제는 회사가 발행한 견적서에 따른
          계좌이체(무통장 입금)로 하며, 입금 확인 후 이용 기간이 개시·연장되고
          거래명세서가 발행됩니다. ③ 보호자·교직원 개인에게는 이용료를 청구하지 않습니다.
        </p>
      </Article>

      <Article title="제7조 (이용 기간과 갱신)">
        <p>
          ① 이용 기간은 입금 확인일(또는 합의한 개시일)부터 12개월입니다. ② 만료 전
          안내에 따라 재계약으로 갱신하며, 자동 결제는 없습니다. ③ 만료 후에도 기존
          기록의 열람은 보장됩니다(아동 기록 접근권 보호).
        </p>
      </Article>

      <Article id="refund" title="제8조 (청약철회와 환불 정책)">
        <p>
          ① <b>서비스 개시 전</b>: 이용 개시일 전에는 언제든지 계약을 철회할 수 있으며,
          받은 금액 전부를 지체 없이 환불합니다.
        </p>
        <p>
          ② <b>서비스 개시 후</b>: 기관이 중도 해지하는 경우, 총 이용료에서 이용한
          기간(개시일부터 해지일까지, 월 단위로 계산하되 1개월 미만은 1개월로 봄)에
          해당하는 금액을 뺀 잔액을 환불합니다.
        </p>
        <p>
          ③ <b>회사 귀책으로 인한 해지</b>: 회사의 귀책 사유로 서비스를 정상 제공하지
          못해 해지하는 경우, 남은 기간에 해당하는 금액 전부를 환불하며 이용자 보호에
          필요한 조치를 다합니다.
        </p>
        <p>
          ④ 환불은 해지 확정일부터 영업일 기준 7일 이내에 계약 시 지정한 계좌로
          지급합니다. ⑤ 이 조에서 정하지 않은 사항은 전자상거래 등에서의 소비자보호에
          관한 법률과 소비자분쟁해결기준을 따릅니다. ⑥ 환불 문의는 서비스 하단에 표기된
          연락처로 접수합니다.
        </p>
      </Article>

      <Article title="제9조 (이용자의 의무)">
        <p>
          이용자는 ① 타인의 계정·초대코드를 도용하지 않고 ② 아동·가정의 사진과 기록을
          서비스 목적 밖으로 유출하지 않으며 ③ 서비스 운영을 방해하는 행위를 하지 않아야
          합니다. 위반 시 회사는 이용을 제한할 수 있습니다.
        </p>
      </Article>

      <Article title="제10조 (개인정보 보호)">
        <p>
          회사는 개인정보 보호법 등 관계 법령을 준수하며, 자세한 내용은 개인정보
          처리방침에 따릅니다. 아동 기록의 열람 범위는 보호자 본인 자녀로 제한됩니다.
        </p>
      </Article>

      <Article title="제11조 (서비스의 변경·중단)">
        <p>
          ① 회사는 운영·기술상 필요에 따라 서비스 내용을 변경할 수 있으며, 중요한 변경은
          미리 공지합니다. ② 점검·장애 등 부득이한 경우 서비스 제공을 일시 중단할 수
          있고, 상당 기간 계속되는 중단은 제8조 ③항을 준용합니다.
        </p>
      </Article>

      <Article title="제12조 (면책)">
        <p>
          회사는 천재지변, 이용자 귀책 사유, 기관이 작성한 기록의 내용 등 회사가 통제할
          수 없는 사유로 생긴 손해에 대해서는 책임을 지지 않습니다. 발달기록은 관찰
          기록이며 의학적 진단·판정이 아닙니다.
        </p>
      </Article>

      <Article title="제13조 (분쟁 해결과 관할)">
        <p>
          서비스 이용과 관련해 분쟁이 생기면 회사와 이용자는 성실히 협의하며, 협의가
          이루어지지 않으면 민사소송법에 따른 관할 법원에 제소합니다.
        </p>
      </Article>

      <Article title="부칙">
        <p>이 약관은 {EFFECTIVE_DATE}부터 적용됩니다.</p>
      </Article>

      <SiteFooter />
    </main>
  );
}
